class DocumentServices {
  private readonly baseUrl: string = process.env.REACT_APP_DMS_URL;
  protected loading: boolean = false;
  protected error: string | null = null;

  isLoading = () => this.loading;
  isError = () => this.error;
  documentUrl = () => this.baseUrl;

  getDocumentFromUrl = async (
    url: string,
    name: string = "document",
    type?: string
  ): Promise<File | null> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      const blob = await response.blob();
      return new File([blob], name, { type: type || blob.type });
    } catch (error) {
      console.error("Error fetching document from URL:", error);
      return null;
    }
  };

  compressImage = async (
    file: File,
    quality = 0.5,
    type = file?.type
  ): Promise<File> => {
    try {
      if (file?.size < 1000000) {
        return Promise.resolve(file);
      }

      // Get as image data
      const imageBitmap = await createImageBitmap(file);

      // Draw to canvas
      const canvas = document.createElement("canvas");
      canvas.width = imageBitmap.width;
      canvas.height = imageBitmap.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(imageBitmap, 0, 0);

      // Turn into Blob
      const blob = (await new Promise((resolve) =>
        canvas.toBlob(resolve, type, quality)
      )) as Blob;

      // Turn Blob into File
      return Promise.resolve(
        new File([blob], file.name, {
          type: blob.type,
        })
      );
    } catch (error) {
      Promise.reject(error.message);
    }
  };

  uploadDocument = async (
    file: File,
    referenceId: string,
    parentType: string,
    path: string,
    session_jwt: string,
    onSuccess?: (response: DocLinkDetails) => void
  ): Promise<DocLinkDetails> => {
    try {
      this.error = null;
      this.loading = true;
      const formData = new FormData();
      formData.append(path, file);
      const apiResponse = await fetch(this.baseUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session_jwt}`,
        },
        body: formData,
      });

      const response = (await apiResponse.json()) as GeneralAPIResponse;

      if (!response.success) {
        throw new Error(response.message);
      }

      const docResponse = response.data as DocumentUploadDetails;

      const docLinkDetails: DocLinkDetails = {
        parentId: referenceId,
        parentType: parentType,
        path: path,
        title: file.name,
        url: `${this.baseUrl}/${docResponse.id}`,
      };

      this.loading = false;
      onSuccess?.(docLinkDetails);
      return Promise.resolve(docLinkDetails);
    } catch (error) {
      console.error("Error uploading document: ", error);
      this.error = error.message;
      return Promise.reject(error);
    }
  };

  replaceDocument = async (
    file: File,
    referenceId: string,
    parentType: string,
    path: string,
    session_jwt: string,
    onSuccess?: (response: DocLinkDetails) => void
  ): Promise<DocLinkDetails> => {
    try {
      this.error = null;
      this.loading = true;
      const formData = new FormData();
      formData.append(path, file);
      const apiResponse = await fetch(`${this.baseUrl}?referenceId=${path}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${session_jwt}`,
        },
        body: formData,
      });

      const response = (await apiResponse.json()) as GeneralAPIResponse;

      this.loading = false;
      if (!response.success) {
        throw new Error(response.message);
      }

      const docResponse = response.data as DocumentUploadDetails;

      const docLinkDetails: DocLinkDetails = {
        parentId: referenceId,
        parentType: parentType,
        path: path,
        title: file.name,
        url: `${this.baseUrl}/${docResponse.id}`,
      };

      onSuccess?.(docLinkDetails);
      return Promise.resolve(docLinkDetails);
    } catch (error) {
      this.error = error.message;
      return Promise.reject(error);
    }
  };
}

const DMSServices = new DocumentServices();

export { DMSServices, DocumentServices };
